import { Box, Stack, Typography, useTheme } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';

import DotLabel from 'components/DotLabel';
import PhoneCtaButton from 'components/PhoneCtaButton';
import Seo from 'components/Seo';
import ServiceCard from 'components/ServiceCard';
import TirePanel from 'components/TirePanel';
import TrustStrip from 'components/TrustStrip';
import Link from 'components/Link';
import { services } from 'pages/Services/utils';
import { glowColor, panelBorder } from 'theme/colors';

const HIGHLIGHTED_SERVICE_IDS = new Set([11, 15, 4, 5, 10, 3]);
const highlightedServices = services.filter(({ id }) => HIGHLIGHTED_SERVICE_IDS.has(id));

const ACCENT_COLOR = 'secondary.main';
const BODY_TEXT_COLOR = 'rgba(238,240,242,0.75)';

const Root: React.FC = () => {
  const theme = useTheme();

  return (
    <>
      <Seo
        title='Jyväskylän Huoltoykköset - Etusivu'
        description='Jyväskylän Huoltoykköset: Edullinen korjaamo Jyväskylä, autohuolto Jyväskylä, autopalvelu Jyväskylä'
        name='Jyväskylän Huoltoykköset: Etusivu'
        type='website'
      />

      <Stack spacing={3} sx={{ pt: { xs: 3, md: 4 }, pb: 5 }}>
        <Stack spacing={2} sx={{ maxWidth: '640px' }}>
          <DotLabel label='Palvelut' />
          <Typography variant='h2'>Palvelumme kattavat kaiken autosi hyvinvoinnin</Typography>
          <Typography variant='p3' sx={{ color: BODY_TEXT_COLOR }}>
            Yksinkertaisista teknisistä huolloista raskaisiin korjaustoimenpiteisiin — yhdeltä luukulta.
          </Typography>
        </Stack>

        <TrustStrip />

        <Box
          display='grid'
          gridTemplateColumns='repeat(3, 1fr)'
          gap={2.25}
          sx={{
            [theme.breakpoints.down('md')]: {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },
            [theme.breakpoints.down('sm')]: {
              gridTemplateColumns: 'repeat(1, 1fr)',
            },
          }}
        >
          {highlightedServices.map(({ id, title, component }) => (
            <ServiceCard key={id} title={title} icon={component} />
          ))}
        </Box>

        <Box
          component={Link}
          to='/services'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            color: ACCENT_COLOR,
            textDecoration: 'none',
            width: 'fit-content',
          }}
        >
          <Typography variant='p3b'>Katso kaikki palvelut →</Typography>
        </Box>
      </Stack>

      <Box
        display='grid'
        gridTemplateColumns='1fr 1.4fr'
        sx={{
          border: panelBorder,
          borderRadius: '14px',
          overflow: 'hidden',
          mb: 5,
          [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
          },
        }}
      >
        <TirePanel minHeight={200} />
        <Stack spacing={1.5} sx={{ p: { xs: 3, md: 4 }, justifyContent: 'center' }}>
          <DotLabel label='Renkaat' />
          <Typography variant='h2' sx={{ fontSize: { xs: 22, md: 26 } }}>
            Rengasmyynti ja rengashotelli
          </Typography>
          <Typography variant='p3' sx={{ color: BODY_TEXT_COLOR }}>
            Kunnon renkaat ovat halpa henkivakuutus! Tule käymään niin tarkistamme veloituksetta renkaasi — ja säilytämme ne
            rengashotellissamme kuin omiamme.
          </Typography>
          <Box component={Link} to='/wheels' sx={{ color: ACCENT_COLOR, textDecoration: 'none', width: 'fit-content' }}>
            <Typography variant='p3b'>Katso rengaspalvelut →</Typography>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderTop: panelBorder,
          borderBottom: panelBorder,
          mx: { xs: -1.5, md: -4.5, lg: -19.5, xl: -25.5 },
        }}
      >
        <Box
          aria-hidden='true'
          sx={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(ellipse at 20% 50%, ${glowColor}, transparent 60%)`,
            pointerEvents: 'none',
          }}
        />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          sx={{
            position: 'relative',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            px: { xs: 1.5, md: 4.5, lg: 19.5, xl: 25.5 },
            py: { xs: 4, md: 5 },
          }}
        >
          <Typography variant='h2' sx={{ maxWidth: '20ch' }}>
            Autosi ansaitsee luotettavan huoltajan.
          </Typography>
          <PhoneCtaButton phone='0452393591' label='Soita 045 239 3591' icon={<PhoneIcon sx={{ fontSize: 18 }} />} />
        </Stack>
      </Box>
    </>
  );
};

export default Root;
